// advHuntingHelpers — group a Defender Advanced Hunting row (log_type 'adv_hunting') into
// labelled sections for the Source Event (Microsoft data) panel.
//
// The pipeline writes every hunting query into ONE wide union of columns, so a single row
// carries ~90 keys of which a handful are filled (a LocalAdminGroupChanges row fills about
// fifteen). Rendering them all is noise; rendering a fixed list drops whatever the next
// query fills. So: known columns go into named sections, empty values are skipped, and any
// other filled column lands in "Other fields" — nothing Microsoft sent is hidden.
//
// This is a view of the raw row, not an interpretation of it. For a kind with alert facts
// (contract §4.4) the meaning is the AlertFactsPanel's job; this panel shows what Defender
// recorded, under Defender's own column names.

// [key, label] per section, in display order.
export const ADV_HUNTING_SECTIONS = [
  {
    id: 'event', title: 'Event', fields: [
      ['Timestamp', 'Timestamp'],
      ['ActionType', 'Action type'],
      ['detection_name', 'Hunting query'],
      ['DeviceName', 'Device name'],
      ['DeviceId', 'Device ID'],
      ['ReportId', 'Report ID'],
    ],
  },
  {
    id: 'account', title: 'Account', fields: [
      ['AccountName', 'Account name'],
      ['AccountDomain', 'Account domain'],
      ['AccountSid', 'Account SID'],
      ['AccountUpn', 'Account UPN'],
      ['AccountObjectId', 'Account object ID'],
      ['OnPremSid', 'On-premises SID'],
      ['CloudSid', 'Cloud SID'],
      ['DirectoryDomain', 'Directory domain'],
      ['DirectoryAccount', 'Directory account'],
      ['GivenName', 'Given name'],
      ['Surname', 'Surname'],
      ['AddedAccountSID', 'Added account SID'],
      ['LocalGroup', 'Local group'],
      ['AccountName1', 'Account name (2)'],
      ['AccountDomain1', 'Account domain (2)'],
      ['AccountSid1', 'Account SID (2)'],
    ],
  },
  {
    id: 'initiator', title: 'Done by (initiating process)', fields: [
      ['Actor', 'Actor'],
      ['InitiatingProcessAccountName', 'Account name'],
      ['InitiatingProcessAccountDomain', 'Account domain'],
      ['InitiatingProcessAccountSid', 'Account SID'],
      ['InitiatingProcessAccountUpn', 'Account UPN'],
      ['InitiatingProcessAccountObjectId', 'Account object ID'],
      ['InitiatingProcessLogonId', 'Logon ID'],
      ['InitiatingProcessFileName', 'Process'],
      ['InitiatingProcessFolderPath', 'Process path'],
      ['InitiatingProcessCommandLine', 'Command line'],
      ['InitiatingProcessSHA256', 'Process SHA-256'],
      ['InitiatingProcessSHA1', 'Process SHA-1'],
      ['InitiatingProcessParentFileName', 'Parent process'],
      ['IsInitiatingProcessRemoteSession', 'Remote session'],
      ['InitiatingProcessRemoteSessionDeviceName', 'Remote session device'],
      ['InitiatingProcessRemoteSessionIP', 'Remote session IP'],
    ],
  },
  {
    id: 'process', title: 'Process / file', fields: [
      ['FileName', 'File name'],
      ['FolderPath', 'Folder path'],
      ['ProcessCommandLine', 'Command line'],
      ['ProcessTokenElevation', 'Token elevation'],
      ['SHA256', 'SHA-256'],
      ['SHA1', 'SHA-1'],
      ['MD5', 'MD5'],
      ['FileOriginUrl', 'File origin URL'],
      ['FileOriginIP', 'File origin IP'],
      ['IsProcessRemoteSession', 'Remote session'],
      ['ProcessRemoteSessionDeviceName', 'Remote session device'],
      ['ProcessRemoteSessionIP', 'Remote session IP'],
    ],
  },
  {
    id: 'network', title: 'Network', fields: [
      ['RemoteIP', 'Remote IP'],
      ['RemotePort', 'Remote port'],
      ['RemoteUrl', 'Remote URL'],
      ['RemoteDeviceName', 'Remote device'],
      ['LocalIP', 'Local IP'],
      ['LocalPort', 'Local port'],
    ],
  },
  {
    id: 'registry', title: 'Registry', fields: [
      ['RegistryKey', 'Key'],
      ['RegistryValueName', 'Value name'],
      ['RegistryValueData', 'Value data'],
    ],
  },
];

// Pipeline plumbing, not Microsoft data (the Raw Event JSON panel still shows them).
const PLUMBING = new Set(['Id', 'tenant_id', 'log_type', 'processed_utc', '_device_in_drmm', 'AdditionalFields']);

// Microsoft writes 0 into a process / parent / logon id it did not record.
const ZERO_MEANS_UNSET = /(Id|SessionId)$/;
const FLAG_KEY = /^Is[A-Z]/;

const IP_KEYS = new Set(['RemoteIP', 'LocalIP', 'FileOriginIP', 'InitiatingProcessRemoteSessionIP', 'ProcessRemoteSessionIP']);
const MONO = /(Sid|SID|Id|SHA|MD5|Upn|CommandLine|Path|Key)$/;

/** Value as display text, or null when it carries nothing. */
export function advHuntingValue(key, value) {
  if (value == null) return null;
  if (typeof value === 'object') return null; // `{}` = a numeric column Microsoft left empty
  if (typeof value === 'string') {
    const t = value.trim();
    if (t === '' || (t === '0' && ZERO_MEANS_UNSET.test(key))) return null;
    return value;
  }
  if (FLAG_KEY.test(key)) return Number(value) === 1 || value === true ? 'Yes' : null;
  if (typeof value === 'number' && value === 0 && ZERO_MEANS_UNSET.test(key)) return null;
  return String(value);
}

/** AdditionalFields is a JSON string of query-specific columns. Returns [[key, text]]. */
export function parseAdditionalFields(value) {
  if (value == null || value === '') return [];
  let obj = value;
  if (typeof value === 'string') {
    try { obj = JSON.parse(value); } catch { return [['AdditionalFields', value]]; }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
  return Object.entries(obj)
    .map(([k, v]) => [k, v != null && typeof v === 'object' ? JSON.stringify(v) : advHuntingValue(k, v)])
    .filter(([, v]) => v != null && v !== '');
}

/** Group a row into sections: [{ id, title, rows: [{ key, label, value, mono, ip, time }] }].
 *  Empty sections are dropped. Returns [] for a non-object. */
export function buildAdvHuntingSections(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
  const used = new Set(PLUMBING);
  const row = (key, label, value) => ({
    key, label, value,
    mono: MONO.test(key),
    ip: IP_KEYS.has(key),
    time: key === 'Timestamp',
  });

  const sections = ADV_HUNTING_SECTIONS.map(({ id, title, fields }) => {
    const rows = [];
    for (const [key, label] of fields) {
      used.add(key);
      const v = advHuntingValue(key, raw[key]);
      if (v != null) rows.push(row(key, label, v));
    }
    return { id, title, rows };
  });

  const extra = parseAdditionalFields(raw.AdditionalFields)
    .map(([k, v]) => row(k, k, v));
  sections.push({ id: 'additional', title: 'Additional fields', rows: extra });

  const other = [];
  for (const [key, value] of Object.entries(raw)) {
    if (used.has(key) || key.includes('@odata.')) continue;
    const v = advHuntingValue(key, value);
    if (v != null) other.push(row(key, key, v));
  }
  sections.push({ id: 'other', title: 'Other fields', rows: other });

  return sections.filter((s) => s.rows.length > 0);
}
